import { useBrandStore, useUserStore } from '@bv/stores'
import { sip } from '@bv/utils'
import { defineStore } from 'pinia'
import moment from 'moment'
import userDetails from '@/api/auth/user-details.api'
import { getItem, setItem, removeItem } from '@/helpers/cookies/cookies.helper'
import PermissionHelper from '@/helpers/auth/permissions.helper'
import { cloneDeep } from 'lodash'
import { useQueuesDataStore } from '@/stores/reports/home.store'

export const userDetailsStore = defineStore('UserDetails', {
  state: () => ({
    userData: null,
    userRoles: null,
    userExtension: null,
    accessedModules: null,
    currentWorkspace: null,
    currentService: null,
    _userWorkspaceServiceDetails: {},
    _currentExtension: {},
    _normalizerConfig: undefined,
    _driver: {},
    uiFlags: {
      detailsFetched: false,
      rolesFetched: false,
      userDetailsError: false,
      userRoleError: false
    }
  }),
  getters: {
    isActiveBilling(state) {
      return (
        !!state.currentService &&
        moment().isSameOrAfter(
          moment(state.currentService.start_date, 'YYYY-MM-DD').startOf('day')
        ) &&
        moment().isSameOrBefore(moment(state.currentService.end_date, 'YYYY-MM-DD').endOf('day'))
      )
    },
    getUserRoles(state) {
      return state.userRoles
    },
    getUserData(state) {
      return state.userData
    },
    getUserExtension(state) {
      return state.userExtension
    },
    getAccessedModules(state) {
      return state.accessedModules
    },
    getUiFlags(state) {
      return state.uiFlags
    },
    getToken() {
      let token

      const impersonateToken = getItem('impersonate_token')

      if (import.meta.env.VITE_IMPERSONATE === 'true' && impersonateToken) {
        token = impersonateToken
      } else {
        token = getItem('token')
      }

      return token
    },
    getWorkspaces(state) {
      return state.userData?.workspaces
    },
    getCurrentWorkspace(state) {
      return state.currentWorkspace
    },
    driver(state) {
      return state._driver
    },
    getCurrentService(state) {
      return state.currentService
    },
    hasValidAiService(state) {
      return state._userWorkspaceServiceDetails.settings?.ai_service?.enabled
    },
    canAccessAiService(state) {
      return state.hasValidAiService && PermissionHelper.checkModuleAuth('ai-call-center')
    },
    isCampaignEnabled(state) {
      return !!state._userWorkspaceServiceDetails.settings?.dialer_service?.enabled
    },
    hasChatService(state) {
      return !!state.currentWorkspace.services.find((s) => s.unique_name === 'chat-business')
    },
    userWorkspaceServiceDetails(state) {
      return state._userWorkspaceServiceDetails
    },
    currentExtension(state) {
      return state._currentExtension
    },
    normalizerConfig(state) {
      return (
        state._normalizerConfig || {
          country_configs: {},
          fallback_country: ''
        }
      )
    }
  },
  actions: {
    async getUserDataRequest() {
      try {
        const data = await useUserStore().getUserDetails()

        this.userData = data
        this.setCurrentWorkspace()

        this.currentService = this.currentWorkspace?.services.find(
          (service) => service.unique_name === 'call-center'
        )

        await this.getExtensionDetails()
      } catch (e) {
        this._currentExtension = {}
        this.uiFlags.userDetailsError = true
      } finally {
        this.uiFlags.detailsFetched = true
      }
    },
    async getUserRolesRequest() {
      try {
        const data = await useUserStore().getService('call-center')
        this.userExtension = { ...data }
        this._userWorkspaceServiceDetails = cloneDeep(data)
        delete this.userExtension?.permissions
        this.userRoles = data?.permissions
        this.accessedModules = data?.can_be_accessed_modules
      } catch (e) {
        console.log(e)
        this.uiFlags.userRoleError = true
      } finally {
        this.uiFlags.rolesFetched = true
      }
    },
    async getExtensionDetails() {
      return useUserStore()
        .getExtensionDetails()
        .then((extensionDetails) => {
          this._currentExtension = extensionDetails
        })
        .catch(() => {
          this._currentExtension = {}
        })
    },
    assignExtension() {
      setTimeout(async () => {
        await this.getUserRolesRequest()
        sip.register()
      }, 5000)
    },
    unAssignExtension() {
      setTimeout(async () => {
        await this.getUserRolesRequest()
        sip.unregister()
      }, 5000)
    },
    logout() {
      useQueuesDataStore().clearPayload()
      userDetails.create({}, 'users/logout').finally(() => {
        const brand = useBrandStore().config
        if (brand) {
          window.location.href = `https://${brand.subdomains.console}.${brand.domain}`
        }
      })
      removeItem('x-workspace-id', `.${brand.domain}`)

      const impersonateToken = getItem('impersonate_token')

      if (import.meta.env.VITE_IMPERSONATE === 'true' && impersonateToken) {
        removeItem('impersonate_token', `.${brand.domain}`)
      } else {
        removeItem('token', `.${brand.domain}`)
      }
    },
    setCurrentWorkspace(newWorkspaceId = null) {
      const workspaceId =
        newWorkspaceId ||
        (this.userData?.recent_workspaces
          ? this.userData?.recent_workspaces[this.userData.recent_service]
          : this.userData?.workspaces[0].id) ||
        getItem('x-workspace-id')

      if (this.currentWorkspace && this.currentWorkspace.id === workspaceId) {
        // No updates in current workspace
        return
      }

      const workspace =
        this.userData.workspaces.find((workspace) => workspace.id == workspaceId) ||
        this.userData.workspaces[0]

      if (workspace) {
        // Set current workspace
        console.log('workspace', workspace)
        this.currentWorkspace = cloneDeep(workspace)
        setItem('x-workspace-id', workspaceId)
      } else {
        // unset current workspace
        this.currentWorkspace = null
        removeItem('x-workspace-id')
      }
    }
  }
})
